//NUOVO loader.service

import { Injectable, OnDestroy } from "@angular/core";
import { LoadingController } from "@ionic/angular";
import { Subscription } from "rxjs";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { AlertService } from "./alert.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { ThemeService } from "./theme.service";

@Injectable({
  providedIn: "root",
})
/**
 * This service manage default ionic loader
 * See ionic docs : https://ionicframework.com/docs/api/loading
 *
 */
export class LoaderService implements OnDestroy {
  private _logContext: string = "Loader.service";
  /**General purpose loader */
  private _currentLoaderWaiting: HTMLIonLoadingElement = null;
  private _currentLoaderBlockAuthentication: any = null;
  private _isGeneralLoaderPresented: boolean = false;

  public _eventSubscriptions: Array<Subscription> = null;

  /**
   * Public setter for dynamically change message loader content
   */
  set messageLoaderBlockAutentication(message: string) {
    if (this._currentLoaderBlockAuthentication) {
      this._currentLoaderBlockAuthentication.message = message;
    }
  }

  constructor(
    private _loadingController: LoadingController,
    private _alertService: AlertService,
    private _loggerService: LoggerService,
    private _eventService: EventService,
    private _themService: ThemeService
  ) { }

  ngOnDestroy() { }

  /**
   * Creates and presents a loader, making sure that it shall be properly dismissed at the end of the defined duration.
   *
   * WARNING: The method shall NOT return UNTIL THE CREATION AND THE PRESENTATION of the loader, while the automatic dismissal
   * shall be hadled by a parallel, asyncrone promise. Consider this when thinking about invocking it
   * with an "await".
   *
   * @param duration Customised duration of the loader; if not specified, is set to 0 ms.
   * @returns
   */
  public async presentLoaderWaiting(duration: number = 0): Promise<void> {

    let nSpinnerElemsPres: number = 0;
    nSpinnerElemsPres = document.getElementsByClassName("loading-spinner").length;

    if (nSpinnerElemsPres >= 1) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "presentLoaderWaiting()",
        "ATTENTION! Spinner overlapping! Spinner number: " + nSpinnerElemsPres
      );
    }

    if (
      !this._isGeneralLoaderPresented &&
      !this.isConnectionHigherPrioriptyPopupArePresented() &&
      nSpinnerElemsPres <= 0
    ) {

      this._isGeneralLoaderPresented = true;

      this._currentLoaderWaiting = await this.createLoaderObject(duration);

      await this._currentLoaderWaiting.present();
    }
  }

  /**
   * Perform close loader; useful if you desires to make sure that a loader is definetely dismissed, or you want
   * to dismiss it BEFORE its natural duration expiration.
   * WARNING: If the "IsUndomitable" flag is set to TRUE, then this method shall NOT work. The loader shall be dismissed (automatically) by
   * the private method "dismissLoadingProperly()" of loader.service.
   */
  public async dismissLoaderWaiting(): Promise<void> {

    if (this._currentLoaderWaiting) {
      await this._currentLoaderWaiting.dismiss();

      this._currentLoaderWaiting = null;
      this._isGeneralLoaderPresented = false;
    }
  }

  /**
   * Present loader controller to start countdown to block user interaction in case of user wrong username or password
   *
   * @param message Initial message
   * @param duration Duration for autoclose
   * @returns
   */
  public async presentLoaderBlockAuthentication(
    message: string,
    duration: number
  ) {
    if (duration > 0) {
      this._currentLoaderBlockAuthentication =
        await this._loadingController.create({
          spinner: "circular",
          cssClass: "loader-block-authentication",
          message,
          keyboardClose: false,
          duration,
        });
    }

    return await this._currentLoaderBlockAuthentication.present();
  }

  //#region  Event Service
  /**
   * Subscribe event from event service
   */
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnReloadBackgroundSupervision.subscribe(() => {
        this.dismissLoaderWaiting();
      })
    );
  }

  /**
   * Unsubscribe event from event service
   */
  public unsubscibeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#endregion

  /**
   * Create general loader object.
   * It duration is set to 0, loader must be closed manually
   *
   * @param duration Loader duration
   * @returns
   */
  private async createLoaderObject(duration: number): Promise<any> {
    const srcLogoLoader: string =
      this._themService.getLogoLoaderByApplicationTheme();

    return await this._loadingController.create({
      spinner: "dots",
      message: '<img src="' + srcLogoLoader + '" />',
      cssClass: "container-loadere-waiter",
      keyboardClose: false,
      duration,
    });
  }

  /**
   * Check if some alert with higher priority (like connections popup are presented)
   *
   * @returns
   */
  private isConnectionHigherPrioriptyPopupArePresented(): boolean {
    return (
      this._alertService.isAlertDisconnectPresented ||
      this._alertService.isAlertIpNotAllowedPresented
    );
  }
}
